import { useTranslation } from "react-i18next";
import { KioskStep, KioskStepColumns } from "../../../../../components/kiosk-step";
import { useAudio } from "../../../../../lib/use-audio";
import {
  AlternativeCheckInPanel,
  CardReaderVisualPanel,
  PkvCheckInPanel,
  cardCheckTitleKey,
} from "./card-check-panels";
import { useCardCheckController } from "./use-card-check-controller";

/**
 * Card check step: detects an eGK, reads it, or routes to manual fallback.
 * The header follows the reader state, the alternatives sit in their own
 * column, and the private-insurance QR replaces the card illustration instead
 * of growing the page.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <CardCheckStep />
 * ```
 */
export function CardCheckStep() {
  const { t } = useTranslation();
  useAudio("checkIn-cardCheck");
  const controller = useCardCheckController();
  const pkvKimInfo = controller.showPkvCheckin ? controller.kimInfo : null;

  return (
    <KioskStep
      width="wide"
      title={t(cardCheckTitleKey(controller.cardState, pkvKimInfo !== null))}
      titleTestId="card-check-title"
    >
      <KioskStepColumns
        aside={
          <AlternativeCheckInPanel
            cardState={controller.cardState}
            kimInfo={controller.kimInfo}
            showPkvCheckin={pkvKimInfo !== null}
            onManualFallbackPointerDown={
              controller.handleManualFallbackPointerDown
            }
            onNoCard={controller.handleNoCard}
            onTogglePkvCheckin={controller.handleTogglePkvCheckin}
          />
        }
      >
        {pkvKimInfo ? (
          <PkvCheckInPanel kimInfo={pkvKimInfo} />
        ) : (
          <CardReaderVisualPanel
            cardState={controller.cardState}
            errorMessage={controller.errorMessage}
            detectedCards={controller.detectedCards}
            connectionWarning={controller.connectionWarning}
            onRetry={controller.handleRetry}
          />
        )}
      </KioskStepColumns>
    </KioskStep>
  );
}
